import React from "react";
import * as RPNInput from "react-phone-number-input";
import { getCountryCallingCode } from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";

type PhoneInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "value"> &
  Omit<RPNInput.Props<typeof RPNInput.default>, "onChange"> & {
    onChange?: (value: RPNInput.Value) => void;
  };

const PhoneInput: React.ForwardRefExoticComponent<PhoneInputProps> = React.forwardRef<
  React.ElementRef<typeof RPNInput.default>,
  PhoneInputProps
>(({ className, onChange, ...props }, ref) => {
  return (
    <RPNInput.default
      ref={ref}
      className={cn(
        "flex h-10 w-full items-center overflow-hidden rounded-xl border border-input bg-background transition-all focus-within:border-primary/50 focus-within:ring-2 focus-within:ring-primary/20",
        className
      )}
      flagComponent={FlagComponent}
      countrySelectComponent={CountrySelect}
      inputComponent={InputComponent}
      onChange={(value) => onChange?.(value || ("" as RPNInput.Value))}
      {...props}
    />
  );
});
PhoneInput.displayName = "PhoneInput";

const InputComponent = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...props }, ref) => (
    <Input
      className={cn(
        "h-full rounded-none rounded-e-xl border-0 bg-transparent px-3 text-sm shadow-none focus-visible:ring-0 focus-visible:ring-offset-0",
        className
      )}
      {...props}
      ref={ref}
    />
  )
);
InputComponent.displayName = "InputComponent";

type CountrySelectOption = { label: string; value?: RPNInput.Country };
type CountrySelectProps = {
  disabled?: boolean;
  value?: RPNInput.Country;
  onChange: (value: RPNInput.Country) => void;
  options: CountrySelectOption[];
};

const CountrySelect = ({ disabled, value, onChange, options }: CountrySelectProps) => {
  const countryLabel = options.find((option) => option.value === value)?.label || "Country";
  const callingCode = value ? `+${getCountryCallingCode(value)}` : "Code";

  const handleSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!e.target.value) {
      return;
    }
    onChange(e.target.value as RPNInput.Country);
  };

  return (
    <div className="relative flex h-full min-w-27 items-center border-r border-input/70 bg-muted/30 px-3">
      <select
        disabled={disabled}
        value={value || ""}
        onChange={handleSelect}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        aria-label="Country"
      >
        <option value="">Country</option>
        {options.map((option) => (
          <option key={option.value || "ZZ"} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      <div className="pointer-events-none flex w-full items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {value ? <FlagComponent country={value} countryName={countryLabel} /> : <span className="text-xs">🌐</span>}
          <span className="text-xs font-semibold text-foreground/85">{callingCode}</span>
        </div>
        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
      </div>
    </div>
  );
};

const FlagComponent = ({ country, countryName }: { country: RPNInput.Country; countryName: string }) => {
  const Flag = flags[country];

  return (
    <span className="flex h-4 w-6 overflow-hidden rounded-sm shadow-sm">
      {Flag ? <Flag title={countryName} /> : <span className="h-full w-full bg-muted" />}
    </span>
  );
};

export { PhoneInput };